import {
    DropdownData,
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { list } from '@fitogest/reactive-form-builder/utils';
import { environment } from 'src/environments/environment';

export class ProductoBioFormDefinition {
    public appName = environment.appName;
    public showPrecio = environment.features.showPrecio;
    public showFertisComposicion = environment.features.showFertisComposicion;
    public formType!: FormType;
    public model: Partial<ProductosModel> = {};
    public formFields!: Form<ProductosModel>;
    public fieldNames: FormField<ProductosModel>[] = [];
    public fieldLabels: string[] = [];

    public productos: DropdownData<string> = list(['...', null], ...this.getProductosOrdenados());

    public unidades: DropdownData<string> = list(
        ['...', null],
        'unidades',
        'kg',
        ['L', 'l'],
        'ml',
    );

    constructor(type: FormType = FormType.CREATION, model: Partial<ProductosModel> = {}) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'producto-bio',
            header: 'Datos del Producto Bio',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    private getFormSections(): FormSection<ProductosModel>[] {
        return [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: false,
                fields: this.getHiddenFields(),
                order: 2,
            },
        ];
    }

    private getMainFields(): FormField<ProductosModel>[] {
        const isEditable = this.formType < FormType.DELETION;
        const textInputType = isEditable ? InputType.TEXT : InputType.ONLY_TEXT;
        const numericInputType = isEditable ? InputType.NUMERIC : InputType.ONLY_TEXT;
        const dropdownSearchInputType = isEditable ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT;
        const dropdownInputType = isEditable ? InputType.DROPDOWN : InputType.ONLY_TEXT;

        const fields: FormField<ProductosModel>[] = [
            {
                name: 'nombre',
                label: 'Nombre o Marca Comercial',
                input: {
                    type: dropdownSearchInputType,
                    data: this.productos,
                    isEditable,
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'composicion',
                label: 'Composición',
                input: {
                    type: textInputType,
                    isEditable,
                },
                width: 4,
                conditions: this.conditions({ isVisible: this.showFertisComposicion }),
                order: 2,
            },
            {
                name: 'unidades',
                label: 'Ud Medida',
                input: {
                    type: dropdownInputType,
                    data: this.unidades,
                    isEditable,
                },
                width: 4,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'precio',
                label: this.appName === 'agrotata' ? 'Precio Coste' : 'Precio',
                input: {
                    type: numericInputType,
                    isEditable,
                },
                width: 4,
                conditions: this.conditions({ isVisible: this.showPrecio }),
                order: 4,
            },
            {
                name: 'precio_venta',
                label: 'Precio de Venta',
                input: {
                    type: numericInputType,
                    isEditable,
                },
                width: 4,
                conditions: this.conditions({ isVisible: this.showPrecio && this.appName === 'agrotata' }),
                order: 5,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getHiddenFields(): FormField<ProductosModel>[] {
        return [
            {
                name: 'id',
                label: 'ID',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: true,
                },
                width: 0,
                conditions: this.conditions({ isVisible: false }),
                order: 1,
            },
            {
                name: 'id_cliente',
                label: 'Cliente',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: true,
                },
                width: 0,
                conditions: this.conditions({ isVisible: false }),
                order: 2,
            },
            {
                name: 'tipo',
                label: 'Tipo',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: true,
                    defaultValue: 'bio',
                },
                updateValue: () => this.model.tipo ?? 'bio',
                width: 0,
                conditions: this.conditions({ isVisible: true }),
                order: 3,
            },
        ];
    }

    private registerVisibleFields(fields: FormField<ProductosModel>[]): FormField<ProductosModel>[] {
        for (const field of fields) {
            if (field.conditions.isVisible) {
                this.fieldNames.push(field);
                this.fieldLabels.push(field.label);
            }
        }

        return fields.sort((a, b) => a.order - b.order);
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: false,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }

    private getProductosOrdenados(): string[] {
        return [
            'Suelta de Enemigos Naturales',
            'Feromonas Picudo',
            'P. Persimilis (Spidex)',
            'A. Californicus (Spical)',
            'A. Swirkii (Swirski-mite)',
            'Cryptolaemus montrouzieri (Cryptoline)',
            'Crysoperla sp (Crysoline)',
            'Bioknock',
            'Equisetum arvense',
            'Tigre',
            'Micorriza (Aegis irriga)',
            'Btu-8 (Bactofus)',
            'Vigor Plus (Btu-8)',
            'Ácid plus (Btu-8)',
            'Nematodo Steirnernema carpocapsae (Biorend leñosos)',
            'Nematodo Heterorhabditis bacteriophora (Biorend melolontha)',
            'Nematodo Steirnernema feltiae ( Biorend invernaderos)',
            'Nematodo Steirnernema feltiae (Entonem)',
            'Kira',
            'Cynoil',
            'Extracto canela (Amicosec)',
            'Isr altech',
            'Jabón potásico (Neptasila)',
            'Bacillus thurigiensis israelensis (Vectobac)',
            'Bacillus thurigiensis israelensis granulado(Vectomax)',
            'Piretinas naturales 3% (Pirecris)',
            'Nematodo Heterorhabditis bacteriophora (Biorend trips)',
            'Nematodo Steirnernema carpocapsae (Capsanem)',
            'Trichoderma (Condor)',
            'Anagyrus pseudococci (Anagline)',
            'Isonet z',
            'Veni biosulfur (Azufre 70%)',
            'Cryptobug',
            'Citripar',
            'Aphytis',
            'Orius Laevigatus',
            'Orius Majusculus',
            'Beauveria Bassiana',
            'Rhizobug 250 adulto',
            'Amblyseius Andersoni',
            'Larvas Chrysoperla',
            'Phytoseiulus Persimilis',
            'Trichogramma Achaeae',
            'Pheromona Duponchelia Fovealis',
            'Euzophera Pinguis',
            'Opogona Sacchari',
            'Californicus System',
            'Feromona + Kairomona Rhynchonex (Picudo)',
            'Margaronia Unionalis',
            'Scyphophorus Acupunctatus',
            'Zeuzera Pyrina',
            'Hypoaspis System',
            'Phyllocnistis Citrella',
            'Pupas Anagyrus Vladimirii',
            'Anthocoris Nemoralis',
            'Amblyseius Swirskii',
            'Nesidiocoris Tenuis',
            'Stratiolaelaps Scimitus',
            'Econex Cromática Amarilla 20 X25 CM',
            'Econex Triangular Blanca Plegable (Trampa sin Lámina)',
            'Econex Lámina para Trampa Triangular',
            'Feromonas Prays Citri',
            'Feromonas Planococus Ficus',
            'Placas 20 X 25 CM Negras CAJA contiene 500 Uds.',
        ].sort((a: string, b: string) => a.localeCompare(b));
    }
}
